import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";

import { useMutation, UseMutationResult } from "@tanstack/react-query";

import { AwsUpload } from "hooks/aws/types";
import { useUpload } from "hooks/aws/useUpload";
import { ApiResponse } from "types/api";

import { TEXTS } from "./constants";

type UploadHandler = (url: string) => void;

export type ImageUploaderContextProps = {
  /**
   * @returns True, if the image is in the schema. False, otherwise.
   */
  setImage: (
    name: string,
    file: File | null,
    onUpload?: UploadHandler
  ) => boolean;
  /**
   * @param imageExtra Extra parameters required to generate image upload.
   * @returns An { name, url } array, if all uploads have been successful. Use
   * setUrlsInValues() to update form values with it.
   */
  uploadImages: UseMutationResult<
    { name: string; url: string }[],
    Error,
    Record<string, unknown>
  >;
};

export type ImageUploaderProviderProps<T> = {
  generateImageUpload: (
    args: T & { mimeType: string }
  ) => Promise<ApiResponse<AwsUpload>>;
  imageSchema: Record<string, T>;

  children: ReactNode;
};

export class ImageUploaderError extends Error {
  constructor(message?: string) {
    super(message || TEXTS.GENERIC_UPLOAD_ERROR);
    this.name = "ImageUploaderError";
  }
}

export const ImageUploaderContext = createContext<ImageUploaderContextProps>({
  setImage: () => false,
  uploadImages: {
    error: new ImageUploaderError("Not implemented"),
    isError: true,
    isIdle: false,
    isPending: false,
    isSuccess: false,
    mutate: () => {},
    mutateAsync: async () => {
      throw new ImageUploaderError("Not implemented");
    },
    reset: () => {},
  } as unknown as ImageUploaderContextProps["uploadImages"],
});

export function ImageUploaderProvider<T>({
  generateImageUpload,
  imageSchema,
  children,
}: ImageUploaderProviderProps<T>) {
  const store = useRef<
    Record<string, { file: File; onUpload?: UploadHandler }>
  >({});
  const { mutateAsync: upload } = useUpload();

  const setImage = useCallback<ImageUploaderContextProps["setImage"]>(
    (name, file, onUpload) => {
      if (!(name in imageSchema)) {
        return false;
      }
      if (file !== null) {
        store.current[name] = { file, onUpload };
      } else {
        delete store.current[name];
      }
      return true;
    },
    [imageSchema]
  );

  const uploadImages: ImageUploaderContextProps["uploadImages"] = useMutation({
    mutationFn: async (imageExtra) => {
      try {
        const uploads = await Promise.all(
          Object.keys(store.current).map(async (name) => ({
            name,
            upload: {
              ...(
                await generateImageUpload({
                  ...imageSchema[name],
                  ...imageExtra,
                  mimeType: store.current[name].file.type,
                })
              ).results,
              file: store.current[name].file,
            },
          }))
        );
        const results = await Promise.allSettled(
          uploads.map(async ({ name, upload: u }) => {
            const url = await upload(u);
            const onUpload = store.current[name].onUpload;
            delete store.current[name];
            onUpload?.(url);
            return { name, url };
          })
        );
        const urls = results
          .filter((result) => result.status === "fulfilled")
          .map((result) => result.value);
        if (urls.length !== results.length) {
          const message = TEXTS.AWS_UPLOAD_ERROR.replace(
            "#{count}",
            String(results.length - urls.length)
          ).replace("#{totalCount}", String(results.length));
          throw new ImageUploaderError(message);
        }
        return urls;
      } catch (e) {
        if (e instanceof ImageUploaderError) {
          throw e;
        }
        throw new ImageUploaderError();
      }
    },
  });

  const context = useMemo<ImageUploaderContextProps>(
    () => ({ setImage, uploadImages }),
    [setImage, uploadImages]
  );

  useEffect(() => {
    store.current = {};
  }, [imageSchema]);

  return (
    <ImageUploaderContext value={context}>{children}</ImageUploaderContext>
  );
}
